"use client";

import React from "react";
import { motion } from "framer-motion";
import { Calculator, Stethoscope, Scale, Wrench, Zap, ShoppingBag, Briefcase, Building2, ShieldCheck, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const sectorsList = [{
  id: "accountants",
  title: "Accountants & Financial Advisers",
  description: "Capturing high-intent search traffic for corporate tax advice, payroll, audit services, and specialized wealth management across the UK.",
  iconName: "Calculator",
  highlight: "High-Intent B2B Keywords"
}, {
  id: "dentists",
  title: "Dentists & Private Healthcare",
  description: "Targeting high-value private dental treatments, cosmetic procedures, and localized patient acquisition with precision local SEO.",
  iconName: "Stethoscope",
  highlight: "Local Map Pack & Patient Acquisition"
}, {
  id: "solicitors",
  title: "Solicitors & Legal Services",
  description: "Ranking for competitive legal practice areas including family law, conveyancing, dispute resolution, and commercial litigation.",
  iconName: "Scale",
  highlight: "High-Authority Legal Content"
}, {
  id: "plumbers",
  title: "Plumbers & Heating Engineers",
  description: "Dominating local search results and emergency service queries across designated regional coverage areas.",
  iconName: "Wrench",
  highlight: "Emergency & Local Search Dominance"
}, {
  id: "electricians",
  title: "Electricians & Contractors",
  description: "Driving consistent inbound commercial and domestic leads for EV charging points, rewiring, and industrial electrical installations.",
  iconName: "Zap",
  highlight: "Targeted Regional Lead Gen"
}, {
  id: "ecommerce",
  title: "Ecommerce & Online Retailers",
  description: "Optimizing technical store architecture, collection hierarchy, and product schema to scale sustainable organic revenue.",
  iconName: "ShoppingBag",
  highlight: "Technical Facet & Schema SEO"
}, {
  id: "b2b-tech",
  title: "B2B Tech & Software (SaaS)",
  description: "Capturing buyer intent across complex problem-based searches, direct software comparisons, and decision-stage keywords.",
  iconName: "Briefcase",
  highlight: "Conversion-Focused Pipeline Growth"
}, {
  id: "property",
  title: "Commercial Property & Real Estate",
  description: "Positioning estate agencies, property surveyors, and commercial developers directly in front of buyers and commercial tenants.",
  iconName: "Building2",
  highlight: "High-Ticket Commercial Reach"
}];
const renderIcon = (name: string) => {
  const iconProps = {
    className: "size-6 text-primary"
  };
  switch (name) {
    case "Calculator":
      return <Calculator {...iconProps} />;
    case "Stethoscope":
      return <Stethoscope {...iconProps} />;
    case "Scale":
      return <Scale {...iconProps} />;
    case "Wrench":
      return <Wrench {...iconProps} />;
    case "Zap":
      return <Zap {...iconProps} />;
    case "ShoppingBag":
      return <ShoppingBag {...iconProps} />;
    case "Briefcase":
      return <Briefcase {...iconProps} />;
    case "Building2":
      return <Building2 {...iconProps} />;
    default:
      return <ShieldCheck {...iconProps} />;
  }
};
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Sectors() {
  return <section id="industrysectorsgrid" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(0,102,255,0.06),rgba(255,255,255,0))] pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium">
            Industry Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Custom SEO Strategies Designed for Your Sector
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-default">
            Every market has unique buyer journeys, search behaviors, and competitive hurdles. 
            I build tailored SEO roadmaps shaped by deep knowledge of these key UK industries.
          </p>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {sectorsList.map((sector, i) => <motion.div key={sector.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full bg-card border-border hover:border-primary/40 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md flex flex-col justify-between group">
                <CardHeader className="space-y-4 pb-4">
                  <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                    {renderIcon(sector.iconName)}
                  </div>
                  <CardTitle className="text-xl font-semibold text-foreground font-default leading-snug group-hover:text-primary transition-colors">
                    {sector.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 pt-0 flex-grow flex flex-col justify-between">
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed font-default">
                    {sector.description}
                  </CardDescription>
                  
                  <div className="pt-2 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-foreground/80">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>{sector.highlight}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="mt-16 md:mt-20 rounded-2xl bg-muted/50 border border-border p-8 md:p-12 text-center max-w-4xl mx-auto shadow-sm">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-4">
            <ShieldCheck className="size-4" />
            <span>Direct Consultant Access</span>
          </div>
          <h3 className="text-2xl sm:text-3xl font-semibold text-foreground font-default mb-4">
            Don't see your specific sector listed?
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-8 text-base leading-relaxed">
            I work directly with specialized service providers, regional businesses, and high-growth brands across many niche verticals. Let's analyze your search potential together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">
                Explore Verified Case Studies
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}